import { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, BellRing, Check, Loader2 } from 'lucide-react';
import { apiPut } from '@/lib/api';
import { LEVEL_HINT, LEVEL_LABEL, effectiveLevel, useWatchList } from '@/lib/assistant';
import { cn } from '@/lib/utils';
import type { WatchLevel } from '@/types/assistant';

export const LEVEL_ICON = { all: BellRing, important: Bell, off: BellOff } as const;
const LEVELS: WatchLevel[] = ['all', 'important', 'off'];

const ITEM = 'flex min-h-[44px] cursor-pointer select-none items-start gap-2.5 rounded-sm px-3 py-2 text-[13px] '
  + 'outline-none hover:bg-secondary focus:bg-secondary data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50';

/** 像 YouTube 頻道旁的小鈴鐺：每台裝置、每個場域各自決定要收多少通知。 */
export function SubscribeBell({ scope, scopeKey, name, siteCode, showLabel = false, className }: {
  scope: 'site' | 'device';
  scopeKey: string;
  name: string;
  siteCode?: string | null;
  showLabel?: boolean;
  className?: string;
}) {
  const qc = useQueryClient();
  const { data } = useWatchList();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const { own, inherited, inheritedFrom, level } = effectiveLevel(data, scope, scopeKey, siteCode);
  const save = useMutation({
    mutationFn: (next: WatchLevel | null) => apiPut('/api/v1/notifications/subscriptions', {
      scope_type: scope, scope_key: scopeKey, level: next,
    }),
    onMutate: () => setError(''),
    // 成功才收起選單；失敗時留著，讓錯誤訊息看得到
    onSuccess: () => setOpen(false),
    onError: (e) => setError(e instanceof Error ? e.message : '設定失敗'),
    onSettled: () => qc.invalidateQueries({ queryKey: ['notifications', 'subscriptions'] }),
  });
  const selected = own ?? 'inherit';
  // 換層級時先不收起，等儲存結果；點到目前的選項就照常收起
  const keepOpen = (value: string) => (event: Event) => { if (value !== selected) event.preventDefault(); };
  const Icon = LEVEL_ICON[level];
  const from = inheritedFrom === 'site' ? '場域' : '整體';
  const summary = `${LEVEL_LABEL[level]}${own ? '' : `（沿用${from}設定）`}`;

  return (
    <DropdownMenu.Root open={open} onOpenChange={(next) => { setOpen(next); if (next) setError(''); }}>
      <DropdownMenu.Trigger asChild>
        <button type="button" data-subscribe-bell={level}
          aria-label={`${name} 的通知：${summary}`} title={`通知：${summary}`}
          className={cn(
            'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            showLabel
              ? 'min-h-[36px] border border-border px-3 text-[13px] font-semibold hover:border-muted-foreground max-md:min-h-[44px]'
              : 'h-9 w-9 hover:bg-secondary max-md:h-11 max-md:w-11',
            level === 'all' ? 'text-primary dark:text-accent-foreground' : 'text-muted-foreground hover:text-foreground',
            className,
          )}>
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
          {showLabel && <span>{summary}</span>}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={6} collisionPadding={12}
          className="z-50 w-[260px] rounded-md border border-border bg-card p-1 shadow-lg">
          <DropdownMenu.Label className="truncate px-3 pb-1 pt-2 text-[11.5px] font-semibold text-muted-foreground">
            {name} 的通知
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={selected}
            onValueChange={(value) => save.mutate(value === 'inherit' ? null : value as WatchLevel)}>
            {LEVELS.map((value) => {
              const ItemIcon = LEVEL_ICON[value];
              return (
                <DropdownMenu.RadioItem key={value} value={value} className={ITEM} onSelect={keepOpen(value)}
                  disabled={save.isPending}>
                  <ItemIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">{LEVEL_LABEL[value]}</span>
                    <span className="block text-[11.5px] leading-snug text-muted-foreground">{LEVEL_HINT[value]}</span>
                  </span>
                  <DropdownMenu.ItemIndicator><Check className="mt-0.5 h-4 w-4 text-primary" /></DropdownMenu.ItemIndicator>
                </DropdownMenu.RadioItem>
              );
            })}
            <DropdownMenu.Separator className="my-1 h-px bg-border" />
            <DropdownMenu.RadioItem value="inherit" className={ITEM} onSelect={keepOpen('inherit')} disabled={save.isPending}>
              <span className="min-w-0 flex-1">
                <span className="block font-semibold">沿用{from}設定</span>
                <span className="block text-[11.5px] leading-snug text-muted-foreground">目前是「{LEVEL_LABEL[inherited]}」</span>
              </span>
              <DropdownMenu.ItemIndicator><Check className="mt-0.5 h-4 w-4 text-primary" /></DropdownMenu.ItemIndicator>
            </DropdownMenu.RadioItem>
          </DropdownMenu.RadioGroup>
          {error && <p role="alert" className="px-3 py-2 text-[12px] text-destructive">{error}</p>}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
